// src/components/pages/dialogs/CalendarSettingsDialog.tsx

import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { X } from 'lucide-react';
import type { CalendarSettings } from '@/components/hooks/useCalendarSettings';

interface Props {
  open: boolean;
  onClose: () => void;
  idUsuario: string;
  nombreUsuario?: string;
  settings: CalendarSettings;
  setSettings: React.Dispatch<React.SetStateAction<CalendarSettings>>;
  loadingSettings: boolean;
  saveSettings: (values: CalendarSettings) => Promise<{ ok: boolean }>;
}

// ── DialogTopBar (mismo estilo que el resto de modales) ──
const DialogTopBar = ({ onClose }: { onClose: () => void }) => (
  <button
    onClick={onClose}
    className="absolute top-2.5 right-3 z-10 shrink-0 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
    aria-label="Cerrar"
  >
    <X className="w-5 h-5" />
  </button>
);

export default function CalendarSettingsDialog({
  open,
  onClose,
  nombreUsuario,
  settings,
  setSettings,
  loadingSettings,
  saveSettings,
}: Props) {
  const { t } = useTranslation();

  const handleSave = async () => {
    const resp = await saveSettings(settings);
    if (resp.ok) onClose();
  };

  const toggle = (key: keyof typeof settings) =>
    setSettings((s) => ({ ...s, [key]: !s[key] }));

  const settingKeys: (keyof typeof settings)[] = [
    'mostrar_trabajador',
    'mostrar_servicio',
    'mostrar_telefono',
    'mostrar_email',
    'mostrar_tipo_pago',
    'mostrar_iconos',
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent
        className="
          z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
          [&>button:last-child]:hidden
          flex flex-col
          !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
          w-full max-w-none h-auto max-h-[85vh] rounded-t-2xl rounded-b-none
          data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
          data-[state=open]:duration-300 data-[state=closed]:duration-200
          data-[state=open]:ease-out data-[state=closed]:ease-in
          sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
          sm:w-[95%] sm:max-w-md sm:mx-auto sm:h-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
          sm:data-[state=open]:zoom-in-95 sm:data-[state=closed]:zoom-out-95
          sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=closed]:slide-out-to-bottom-0
        "
      >
        <DialogTopBar onClose={onClose} />

        <DialogHeader className="px-5 pt-4 pb-1 pr-14 shrink-0">
          <DialogTitle className="text-sm font-semibold text-[#103254] dark:text-blue-300">
            {t('calendarSettingsDialog.title')}
          </DialogTitle>
          {nombreUsuario && (
            <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1 font-normal">
              {t('calendarSettingsDialog.worker')}:{' '}
              <strong className="text-gray-700 dark:text-gray-300">
                {nombreUsuario}
              </strong>
            </span>
          )}
        </DialogHeader>

        <div className="px-5 py-3 flex-1 overflow-y-auto">
          {!loadingSettings && (
            <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-5 text-sm text-gray-700 dark:text-gray-300">
              {settingKeys.map((key) => (
                <div key={key} className="flex justify-between items-center">
                  <span className="text-gray-700 dark:text-gray-300">
                    {t(`calendarSettingsDialog.${key}`)}
                  </span>
                  <Switch
                    checked={settings[key]}
                    onCheckedChange={() => toggle(key)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter className="mt-2 px-5 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row sm:justify-end gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loadingSettings}
            className="w-full sm:w-auto rounded-lg font-medium bg-transparent dark:bg-transparent border-gray-300 dark:border-white/10 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
          >
            {t('calendarSettingsDialog.cancel')}
          </Button>

          <Button
            onClick={handleSave}
            disabled={loadingSettings}
            className="w-full sm:w-auto rounded-xl text-white font-semibold text-base shadow-md bg-[#103254] hover:bg-[#0d2742] dark:bg-[#103254] dark:hover:bg-[#0d2742] py-3 disabled:opacity-50 transition-colors"
          >
            {loadingSettings
              ? t('calendarSettingsDialog.saving')
              : t('calendarSettingsDialog.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}